// ----------------------------------------------------------------------------
// Availability Logic for Storeden / TS Commerce products
// ----------------------------------------------------------------------------

export interface RestockItem {
  date: string; // e.g. "06-10-2026"
  quantity: number;
}

export type AvailabilityCase =
  | "available" // CASO 1: Prodotto standard disponibile
  | "restocking" // CASO 2: Prodotto in riassortimento (preordinabile)
  | "out_of_catalog_with_stock" // CASO 3: Fuori catalogo fino a esaurimento scorte
  | "out_of_catalog_exhausted" // CASO 4: Fuori catalogo ed esaurito
  | "unavailable"; // Non disponibile (stock <= 0 e nessun restock)

export interface ProductAvailability {
  caseType: AvailabilityCase;
  isAvailable: boolean;
  canOrder: boolean;
  statusLabel: string;
  detailLabel: string;
  availableQuantity?: number;
  isOutOfCatalog: boolean;
  isRestocking: boolean;
  restockDate?: string;
  restockQuantity?: number;
  allRestockDates: RestockItem[];
  surplus: number;
  badgeVariant: "emerald" | "amber" | "orange" | "red" | "gray";
  notes?: string;
}

export interface AvailabilityProductInput {
  tags?: string[];
  stockCount?: number;
  unlimited?: number;
}

/**
 * Calcola lo stato di disponibilità di un prodotto secondo le specifiche di Storeden.
 * - CASO 1: Disponibile standard e quantità ordinabile
 * - CASO 2: In riassortimento, data presunto arrivo e quantità ordinabile
 * - CASO 3: Fuori catalogo fino a esaurimento scorte con unità disponibili
 * - CASO 4: Fuori catalogo ed esaurito (disponibilità = 0)
 */
export function getProductAvailability(
  product: AvailabilityProductInput | null | undefined,
): ProductAvailability {
  if (!product) {
    return {
      caseType: "unavailable",
      isAvailable: false,
      canOrder: false,
      statusLabel: "Non disponibile",
      detailLabel: "Dati non disponibili",
      isOutOfCatalog: false,
      isRestocking: false,
      allRestockDates: [],
      surplus: 0,
      badgeVariant: "gray",
    };
  }

  const tags = Array.isArray(product.tags) ? product.tags : [];

  // Tag fuori catalogo
  const isOutOfCatalog = tags.some((t) => {
    const lower = String(t).toLowerCase().trim();
    return (
      lower === "fuori-catalogo-fuoricat" ||
      lower === "fuori-catalogo" ||
      lower === "fuoricat" ||
      lower.includes("fuori-catalogo")
    );
  });

  // Estrazione tag restock: "restock:DD-MM-YYYY:QTY"
  const restockTags = tags.filter((t) =>
    String(t).toLowerCase().trim().startsWith("restock:"),
  );

  const stockCount =
    typeof product.stockCount === "number" ? product.stockCount : 0;
  let deficit = stockCount < 0 ? Math.abs(stockCount) : 0;
  let surplus = 0;
  const finalRestockDates: RestockItem[] = [];

  const now = new Date();
  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();

  for (const tag of restockTags) {
    const parts = String(tag).split(":");
    if (parts.length < 3) continue;

    const dateStr = parts[1]!.trim();
    let rQty = parseFloat(parts[2]!.trim()) || 0;

    // Step 1: Copri eventuale deficit
    if (deficit > 0) {
      if (deficit >= rQty) {
        deficit -= rQty;
        rQty = 0;
      } else {
        rQty -= deficit;
        deficit = 0;
      }
    }

    // Step 2: Timestamp confronto
    const dateParts = dateStr.split("-").map((p) => parseInt(p, 10));
    let restockTimestamp = 0;
    if (dateParts.length === 3 && dateParts[0] && dateParts[1] && dateParts[2]) {
      const day = dateParts[0];
      const month = dateParts[1] - 1;
      const year = dateParts[2];
      restockTimestamp = new Date(year, month, day).getTime();
    }

    if (restockTimestamp > 0 && restockTimestamp <= todayMidnight) {
      // Data già passata: merce considerata arrivata (surplus)
      surplus += rQty;
    } else {
      // Data futura: da ricevere
      rQty += surplus;
      surplus = 0;
      if (rQty > 0 && finalRestockDates.length === 0) {
        finalRestockDates.push({ date: dateStr, quantity: rQty });
      }
    }
  }

  // --------------------------------------------------------------------------
  // CASO 2: Prodotto in riassortimento con data futura
  // --------------------------------------------------------------------------
  if (finalRestockDates.length > 0) {
    const nextRestock = finalRestockDates[0]!;
    return {
      caseType: "restocking",
      isAvailable: false,
      canOrder: true,
      statusLabel: "In riassortimento",
      detailLabel: "Previsto riassortimento",
      restockDate: nextRestock.date,
      restockQuantity: nextRestock.quantity,
      availableQuantity: nextRestock.quantity,
      isOutOfCatalog,
      isRestocking: true,
      allRestockDates: finalRestockDates,
      surplus,
      badgeVariant: "amber",
      notes:
        "Se ordinate una quantità maggiore, verrà comunicata la data di riassortimento successivamente.",
    };
  }

  // --------------------------------------------------------------------------
  // Nessun riassortimento futuro: verifica scorte fisiche o surplus
  // --------------------------------------------------------------------------
  const hasPhysicalStock =
    product.unlimited === 1 ||
    surplus > 0 ||
    (product.stockCount !== undefined && product.stockCount > 0);

  const hasExplicitZero =
    product.stockCount !== undefined &&
    product.stockCount <= 0 &&
    surplus <= 0 &&
    product.unlimited !== 1;

  // --------------------------------------------------------------------------
  // Fuori catalogo
  // --------------------------------------------------------------------------
  if (isOutOfCatalog) {
    // Un articolo fuori catalogo si basa esclusivamente sulle scorte residue.
    // L'indicatore 'unlimited' non si applica a prodotti fuori catalogo.
    const qty =
      surplus > 0
        ? surplus
        : Math.max(
            0,
            product.stockCount !== undefined ? Number(product.stockCount) : 0,
          );

    if (qty <= 0) {
      // CASO 4: Fuori catalogo ed esaurito
      return {
        caseType: "out_of_catalog_exhausted",
        isAvailable: false,
        canOrder: false,
        statusLabel: "Fuori catalogo - Esaurito",
        detailLabel: "Non previsto riassortimento",
        availableQuantity: 0,
        isOutOfCatalog: true,
        isRestocking: false,
        allRestockDates: [],
        surplus: 0,
        badgeVariant: "red",
        notes:
          "Prodotto fuori catalogo ed esaurito. Non è più possibile ordinare questo articolo.",
      };
    }

    // CASO 3: Fuori catalogo con disponibilità residua
    return {
      caseType: "out_of_catalog_with_stock",
      isAvailable: true,
      canOrder: true,
      statusLabel: "Disponibile fino a esaurimento scorte",
      detailLabel: "Ultimi pezzi disponibili",
      availableQuantity: qty,
      isOutOfCatalog: true,
      isRestocking: false,
      allRestockDates: [],
      surplus,
      badgeVariant: "orange",
      notes: "",
    };
  }

  // --------------------------------------------------------------------------
  // Prodotto standard (in catalogo)
  // --------------------------------------------------------------------------
  const isUnlimited = (product.unlimited === 1 || (product as any).unlimited === true) && !isOutOfCatalog;
  const stockNum = product.stockCount !== undefined ? Number(product.stockCount) : 0;
  if (!isUnlimited && surplus <= 0 && stockNum <= 0) {
    return {
      caseType: "unavailable",
      isAvailable: false,
      canOrder: false,
      statusLabel: "Non disponibile",
      detailLabel: "Attualmente esaurito",
      availableQuantity: 0,
      isOutOfCatalog: false,
      isRestocking: false,
      allRestockDates: [],
      surplus: 0,
      badgeVariant: "gray",
      notes: "Articolo temporaneamente non a magazzino.",
    };
  }

  // CASO 1: Disponibile standard
  const availableQty =
    surplus > 0
      ? surplus
      : product.unlimited === 1
        ? undefined
        : product.stockCount !== undefined && product.stockCount > 0
          ? product.stockCount
          : undefined;

  return {
    caseType: "available",
    isAvailable: true,
    canOrder: true,
    statusLabel: "Disponibile",
    detailLabel: "In pronta consegna",
    availableQuantity: availableQty,
    isOutOfCatalog: false,
    isRestocking: false,
    allRestockDates: [],
    surplus,
    badgeVariant: "emerald",
  };
}
